import {
    CurrentBody,
    CurrentHeader,
    CurrentTable,
    DynamicInputTable,
    InputBody,
    InputHeader,
    InputTable
} from "../FormComponents/Input/DynamicInputTable";
import {Dropdown} from "../FormComponents/Dropdown";
import CustomerSearch from "../Search/CustomerSearch";
import {productDropdown} from "../../Constants/dropDownLists";
import {CustomerConnectionRow, CustomerConnectionsHeader} from "./CustomerConnectionsTable";
import React, {useEffect, useState} from "react";
import useProductName from "../../Hooks/useProductName";
import {useTranslation} from "react-i18next";
import {bindActionCreators} from "redux";
import commonActions from "../../Actions/commonActions";
import {connect} from "react-redux";
import forms from "../../Constants/forms";

function HeadRow ({}) {
    const { t } = useTranslation();
    return <>
        <th>{t('customerconnections.type')}</th>
        <th>{t('customerconnections.customer')}
            <small>{t('customerconnections.namecustnoorgno')}</small>
        </th>
        <th>{t('customerconnections.product')}</th>
        <th>{t('customerconnections.discount')}</th>
    </>
}

function CustomerConnectionsForm({ form, customerConnections, commonActions, customer, dropdownlists }) {
    const { translatedProductName } = useProductName();
    const { t } = useTranslation();

    const customerSearchRef = React.useRef(null);
    const [selectedProductId, setSelectedProductId] = useState();
    const [selectedType, setSelectedType] = useState();
    const [selectedCustomer, setSelectedCustomer] = useState({});
    const [selectedDiscountId, setSelectedDiscountId] = useState();
    const [current, setCurrent] = useState(customerConnections);
    const dropdown = dropdownlists["CustomerConnectionDiscounts"];
    const subOptions = dropdown?.find(r => r.name === selectedProductId)?.subOptions ?? [];
    let formData = form?.data;
    
    const resetForm = () => {
        commonActions.startFormEditing(forms.customerConnectionsFormData, {toAdd: [], toDelete: []});
    }

    useEffect(() => {
        resetForm();
    }, [])

    useEffect(() => {
        resetForm();
        clearSelected();
        setCurrent(customerConnections);
    }, [customerConnections]);

    const clearSelected = () => {
        setSelectedProductId(null);
        setSelectedType(null);
        setSelectedCustomer(null);
        customerSearchRef.current?.clear();
    }

    const onChange = (value, key) => {
        commonActions.changeMultiFormField(form.id, key, value);
    }
    const onAdd = () => {
        if (subOptions.length > 0 && !selectedDiscountId) {
            commonActions.showError("Discount is required for selected product", "Please validate");
            return;
        }
        
        if (!selectedType || !selectedProductId || !selectedCustomer.customerNumber) return;

        let typeAsNumber = Number(selectedType);
        let newConnectedCustomer = {
            id: customerConnections.find(c => c.customerNumber === selectedCustomer.customerNumber && c.product === selectedProductId && c.type === typeAsNumber)?.id,
            type: typeAsNumber,
            customerNumber: selectedCustomer.customerNumber,
            name: selectedCustomer.name,
            organizationNumber: selectedCustomer.organizationNumber,
            product: selectedProductId,
            discountId: selectedDiscountId
        }

        setCurrent([...current, newConnectedCustomer]);
        const updatedToDelete = formData.toDelete.filter(c => c.customerNumber !== newConnectedCustomer.customerNumber || c.product !== newConnectedCustomer.product || c.type !== newConnectedCustomer.type);

        if (updatedToDelete.length === formData.toDelete.length) {
            onChange([...formData.toAdd, newConnectedCustomer], "toAdd");
        } else if (updatedToDelete.length < formData.toDelete.length) {
            onChange([...updatedToDelete], "toDelete");
        }

        clearSelected();
    }

    const onDelete = (index) => {
        const customer = current[index];
        const updatedCurrent = current.filter((_, i) => i !== index);
        const updatedToAdd = formData.toAdd.filter(c => c.customerNumber !== customer.customerNumber || c.product !== customer.product || c.type !== customer.type);

        setCurrent(updatedCurrent);
        if (updatedToAdd.length < formData.toAdd.length) {
            onChange([...updatedToAdd], "toAdd");
        } else {
            onChange([...formData.toDelete, customer], "toDelete");
        }
    }
    
    const selectProduct = (id) => {
        setSelectedProductId(id);
        setSelectedDiscountId(null)
    }
    
    return <form className="skynet-form container">
        <fieldset>
            {t('customerconnections.addandremove')}
        </fieldset>
        <fieldset className="d-grid col-12 gap-16">
            <DynamicInputTable>
                <InputTable disableAdd={!selectedType || !selectedProductId || !selectedCustomer?.customerNumber} onAdd={onAdd}>
                    <InputHeader>
                        <HeadRow/>
                    </InputHeader>
                    <InputBody>
                        <td className="p-12">
                            <Dropdown options={[{name: t('customerconnections.main'), id: 0}, {name: t('customerconnections.sub'), id: 1}]}
                                      valueProperty={"id"}
                                      property={"name"} placeholder={t('customerconnections.selecttype')}
                                      value={selectedType}
                                      onChange={(value) => setSelectedType(value)}/>
                        </td>
                        <td className="p-12">
                            <CustomerSearch
                                ref={customerSearchRef}
                                id="customerConnectionsSearch"
                                onChange={(customer) => setSelectedCustomer({
                                    customerNumber: customer.customerNumber,
                                    name: customer.name,
                                    organizationNumber: customer.organizationNumber
                                })}
                            />
                        </td>
                        <td className="p-12">
                            <Dropdown options={productDropdown} valueProperty={"id"}
                                      nameAggregate={(p) => translatedProductName(p.tName)} placeholder={t('customerconnections.selectproduct')}
                                      value={selectedProductId}
                                      onChange={(value) => selectProduct(value)}/>
                        </td>
                        <td className="p-12" style={{ maxWidth: 200, minWidth: 200 }}>
                            <Dropdown options={subOptions} valueProperty={"guid"} placeholder={t('customerconnections.selectdiscount')}
                                      value={selectedDiscountId}
                                      disabled={!selectedProductId || subOptions.length === 0}
                                      onChange={(value) => setSelectedDiscountId(value)}/>
                        </td>
                    </InputBody>
                </InputTable>
                <CurrentTable>
                    <CurrentHeader><CustomerConnectionsHeader/></CurrentHeader>
                    <CurrentBody current={current} onDelete={onDelete}>
                        {(connection) => <CustomerConnectionRow connection={connection} customer={customer.vbCustomer}/>}
                    </CurrentBody>
                </CurrentTable>
            </DynamicInputTable>
        </fieldset>
    </form>
}

function mapStateToProps(state) {
    return {
        customer: state.CustomerReducer.customer,
        customerConnections: state.CustomerReducer.customerConnections,
        dropdownlists: state.CommonReducer.dropdownlists,
        form: state.CommonReducer.forms && state.CommonReducer.forms[forms.customerConnectionsFormData]
    }
}


function mapDispatchToProps(dispatch) {
    return {
        commonActions: bindActionCreators(commonActions, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomerConnectionsForm);
